import React, { useEffect, useState } from 'react';
import styles from './VytalHeader.module.css';
import { isInIframe } from '../../utils/iframe';
import {
  sendCloseIframeMessage,
  sendUserLoggedOutMessage,
} from '../../services/webSdkCommunicator';
import { useRouter } from 'next/router';
import VytalLogo from '../../images/svg/Vytal-logo-claim-de-black.svg';
import CloseSVG from '../../images/svg/close.svg';
import { useTranslation } from 'next-i18next';
import { LOCAL_STORAGE_KEYS } from '../../services/localStorage';
import { getWrappedLink } from '../../utils/routerHelper';
import TRANSLATION_FILES from '../../public/locales/constants';
import { usePartnerConfig } from '../../contexts/PartnerConfig';
import { PartnerSlug } from '../../config/partnerConfigs';

export function VytalHeader({
  showLogoutButton = true,
  rightContent,
  alwaysShowVytalLogo = false,
}: {
  showLogoutButton: boolean;
  rightContent?: React.ReactNode;
  alwaysShowVytalLogo?: boolean;
}) {
  const router = useRouter();
  const { t } = useTranslation(TRANSLATION_FILES.common);
  const partnerConfig = usePartnerConfig();
  const logo = partnerConfig?.assets.logo;
  const replaceVytalLogo = partnerConfig?.theme.replaceVytalLogo;

  const isHoiConfig = partnerConfig?.slug === PartnerSlug.HOI;

  const headerStyleOverrides: React.CSSProperties = {};
  if (router.pathname.match(/^\/containers\/.*\/transactions\/.*/)) {
    headerStyleOverrides.paddingLeft = '12px';
  }
  if (isHoiConfig) {
    headerStyleOverrides.backgroundColor = 'var(--hoi-blue)';
  }

  const logoContainerStyleOverrides: React.CSSProperties = {};
  if (isHoiConfig) {
    logoContainerStyleOverrides.boxSizing = 'border-box';
    logoContainerStyleOverrides.width = '148px';
    logoContainerStyleOverrides.paddingTop = '20px';
    logoContainerStyleOverrides.paddingBottom = '20px';
  }

  // show close button if used in Web SDK with an iframe
  // do the check only on client side, so SSR is consistent with first render
  const [showCloseButton, setShowCloseButton] = useState(false);
  useEffect(() => {
    setShowCloseButton(isInIframe());
  }, []);

  const onClose = async () => {
    await sendCloseIframeMessage();
  };

  const onLogoutClick = async () => {
    if (localStorage) {
      localStorage.removeItem(LOCAL_STORAGE_KEYS.VYTAL_ENCRYPTED_JWT);
      localStorage.removeItem(LOCAL_STORAGE_KEYS.VYTAL_ENCRYPTED_REFRESH_TOKEN);
    }

    try {
      await sendUserLoggedOutMessage();
    } catch (e) {
      console.log('onLogoutClick sendUserLoggedOutMessage', e);
    }

    const form = document.getElementById('logout-form') as HTMLFormElement;
    form.submit();
  };

  // suppressHydrationWarning only works one level deep, problem will not go away, this is okay
  // in this case
  return (
    <div
      className="vytal-header"
      suppressHydrationWarning={true}
      style={headerStyleOverrides}
    >
      <div
        className={`vytal-logo-small ${alwaysShowVytalLogo ? '' : 'flex md:hidden'}`}
        style={logoContainerStyleOverrides}
      >
        {replaceVytalLogo && logo && <img src={logo} alt="Logo" />}
        {(!replaceVytalLogo || !logo) && <VytalLogo />}
      </div>
      <form
        id="logout-form"
        method="POST"
        action={getWrappedLink('/welcome/logout', router.query)}
      ></form>
      <div className="flex flex-row items-center space-x-5">
        {showLogoutButton && (
          <a
            className="justify-end vytal-dark font-semibold"
            onClick={onLogoutClick}
          >
            {t('logOut').toUpperCase()}
          </a>
        )}
        {showCloseButton ? (
          <div className={styles.closeButton} onClick={onClose}>
            <CloseSVG />
          </div>
        ) : (
          rightContent
        )}
      </div>
    </div>
  );
}
